import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg, fmt, fmtDate, DELIVERY, STATUS, PAY_METHOD } from "../../lib/api";
import { ScopeSelect, ScopeNote } from "../../components/ScopeSelect";
import { DISTRICTS } from "../../lib/categories";
import { btnGhost, btnPrimary } from "../../components/ui";
import { TOPUP_STATUS, DemoBanner } from "../Recargas";

export function ZonesEditor() {
  const [zones, setZones] = useState(null);
  useEffect(() => { api.get("/admin/zones").then(({ data }) => setZones(data)); }, []);
  if (!zones) return null;
  const set = (i, k, v) => setZones(zones.map((z, j) => (j === i ? { ...z, [k]: v } : z)));
  const save = async () => {
    try { const { data } = await api.put("/admin/zones", { zones: zones.map((z) => ({ ...z, fee: Number(z.fee) })) }); setZones(data); toast.success("Zonas guardadas"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const unused = DISTRICTS.filter((d) => !zones.some((z) => z.district === d));
  return (
    <section className="bo-card flex flex-col gap-2 p-5" data-testid="zones-editor">
      <h3 className="font-bold">Zonas e preços de entrega</h3>
      <p className="text-xs text-gray-500">Sem zona ativa para o distrito, usa-se a taxa de entrega de cada loja.</p>
      {zones.map((z, i) => (
        <div key={z.district} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`zone-row-${i}`}>
          <span className="w-56 font-medium">{z.district}</span>
          <input type="number" min={0} value={z.fee} onChange={(e) => set(i, "fee", e.target.value)} className="w-28 rounded-lg border border-gray-200 px-2 py-1.5" data-testid={`zone-fee-${i}`} />STN
          <label className="flex items-center gap-1"><input type="checkbox" checked={z.active} onChange={(e) => set(i, "active", e.target.checked)} data-testid={`zone-active-${i}`} />Ativa</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={z.delivery !== false} onChange={(e) => set(i, "delivery", e.target.checked)} data-testid={`zone-delivery-${i}`} />Entregas</label>
          <label className="flex items-center gap-1 text-gray-400" title="Transporte de passageiros ainda em preparação"><input type="checkbox" disabled checked={!!z.transport} data-testid={`zone-transport-${i}`} />Transporte (em preparação)</label>
          <button onClick={() => setZones(zones.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500" data-testid={`zone-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        {unused.length > 0 && <select value="" onChange={(e) => e.target.value && setZones([...zones, { district: e.target.value, fee: 0, active: true }])} className="rounded-full border border-gray-200 px-3 py-1.5 text-sm" data-testid="zone-add-select">
          <option value="">+ Adicionar distrito</option>{unused.map((d) => <option key={d}>{d}</option>)}</select>}
        <button onClick={save} className={`${btnPrimary} px-4 py-1.5`} data-testid="zones-save-btn">Guardar zonas</button>
      </div>
    </section>
  );
}

export function DemoModeToggle({ onChange }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/topups/demo").then(({ data }) => { setD(data); onChange(data.enabled); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const toggle = async () => {
    try { const { data } = await api.put("/admin/topups/demo", { enabled: !d.enabled }); setD(data); onChange(data.enabled); toast.success(data.enabled ? "Modo DEMO ativado" : "Modo DEMO desativado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="bo-card flex flex-col gap-2 p-5 text-sm" data-testid="topup-demo-toggle">
      <h3 className="font-bold">Modo demonstração OboWay</h3>
      {d.enabled && <DemoBanner testid="admin-topup-demo-banner" />}
      <p className="text-xs text-gray-500">Pedidos simulados marcados DEMO, sem conta bancária, sem comprovativo, sem ledger financeiro e sem contacto com operadoras. Disponível apenas no ambiente de desenvolvimento.</p>
      {d.allowed ? <button onClick={toggle} className={`${d.enabled ? btnGhost : btnPrimary} w-fit px-4 py-1.5`} data-testid="topup-demo-toggle-btn">{d.enabled ? "Desativar modo DEMO" : "Ativar modo DEMO"}</button>
        : <p className="text-xs font-semibold text-red-600" data-testid="topup-demo-not-allowed">Indisponível fora do ambiente de desenvolvimento.</p>}
    </section>
  );
}

const SalesTable = ({ title, rows, testid }) => (
  <div className="bo-card p-4 text-sm" data-testid={testid}>
    <h3 className="mb-2 font-bold">{title}</h3>
    {!rows?.length && <p className="text-gray-500">Sem vendas no período.</p>}
    {rows?.map((x) => <p key={x.key} className="flex justify-between gap-3 border-b border-gray-50 py-1"><span className="truncate">{x.key || "—"}</span><span className="shrink-0">{x.qty} un. · {fmt(x.total)}</span></p>)}
  </div>
);

const Table = ({ title, data, labels, testid }) => (
  <div className="bo-card p-4 text-sm" data-testid={testid}>
    <h3 className="mb-2 font-bold">{title}</h3>
    {!Object.keys(data).length && <p className="text-gray-500">Sem dados no período.</p>}
    {Object.entries(data).map(([k, v]) => <p key={k} className="flex justify-between border-b border-gray-50 py-1"><span>{labels?.[k] || k}</span><span>{v.count}{v.total != null && ` · ${fmt(v.total)}`}</span></p>)}
  </div>
);

export function AdminReports() {
  const [days, setDays] = useState(30);
  const [r, setR] = useState(null);
  const [scope, setScope] = useState("real");
  useEffect(() => { api.get("/admin/reports", { params: { days, scope } }).then(({ data }) => setR(data)); }, [days, scope]);
  const statusLabels = Object.fromEntries(Object.entries(STATUS).map(([k, v]) => [k, v.label]));
  return (
    <div className="flex flex-col gap-3" data-testid="admin-reports">
      <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-fit rounded-full border px-3 py-1.5 text-sm" data-testid="reports-days-select">
        {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Últimos {d} dias</option>)}</select>
      <div className="flex flex-wrap items-center gap-3"><ScopeSelect value={scope} onChange={setScope} testid="reports-scope-select" /><ScopeNote scope={scope} /></div>
      {r && <div className="grid gap-3 md:grid-cols-2">
        <Table title="Encomendas por estado" data={r.orders_by_status} labels={statusLabels} testid="report-orders-status" />
        <Table title="Encomendas por pagamento" data={r.orders_by_payment} labels={PAY_METHOD} testid="report-orders-payment" />
        <Table title="Entregas por estado" data={r.deliveries_by_status} labels={DELIVERY} testid="report-deliveries-status" />
        <Table title="Entregas concluídas por estafeta" data={r.deliveries_by_courier} testid="report-deliveries-courier" />
        <Table title="Recargas por estado" data={r.topups_by_status} labels={TOPUP_STATUS} testid="report-topups" />
        <SalesTable title="Vendas por loja (pagas, sem reembolsadas)" rows={r.sales_by_store} testid="report-sales-store" />
        <SalesTable title="Vendas por categoria" rows={r.sales_by_category} testid="report-sales-category" />
        <SalesTable title="Produtos mais vendidos" rows={r.top_products} testid="report-top-products" />
      </div>}
      <p className="text-xs text-gray-400">Gerado {fmtDate(new Date().toISOString())}</p>
    </div>
  );
}
